import {
  GUID,
  validateNotNullishObject,
  validateOfType,
} from "@faro-lotv/foundation";
import { clearStore } from "@faro-lotv/project-source";
import {
  CaptureTreeEntityRevision,
  RegistrationEdgeRevision,
  RegistrationMetrics,
  RegistrationRevision,
  validateRegistrationMetrics,
} from "@faro-lotv/service-wires";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  RevisionTransformCache,
  generateRevisionTransformCache,
} from "./revision-transform-cache";

/** Entities by ID. The order of the keys is the order of the siblings in the scan tree. */
export type EntityMap = Record<GUID, CaptureTreeEntityRevision | undefined>;

/** The data we expect to be contained in a registration edge on the capture tree. */
export type RegistrationEdgeMetricsData = {
  metrics: RegistrationMetrics;
};

/**
 * @param data The data to validate
 * @returns true if the data is a valid `RegistrationEdgeMetricsData` object, else false.
 */
export function isRegistrationEdgeMetricsData(
  data: unknown,
): data is RegistrationEdgeMetricsData {
  return (
    validateNotNullishObject<RegistrationEdgeMetricsData>(
      data,
      "RegistrationEdgeData",
    ) && validateOfType(data, "metrics", validateRegistrationMetrics)
  );
}

export type EdgesMap = Record<GUID, RegistrationEdgeRevision | undefined>;

type RevisionState = {
  /** The information about registration revision */
  registrationRevision: RegistrationRevision | undefined;

  /** A map from IDs to the corresponding entity in the revision. */
  entityMap: EntityMap;

  /** A map from IDs to the corresponding registration edge in the revision. */
  edgesMap: EdgesMap;

  /** Whether the edges are currently being loaded. */
  areEdgesLoading: boolean;

  /** The cached world transforms of the entities. */
  transformCache: RevisionTransformCache;
};

export const initialState: Readonly<RevisionState> = Object.freeze({
  registrationRevision: undefined,
  entityMap: {},
  edgesMap: {},
  areEdgesLoading: true,
  transformCache: {},
  visibilityMap: {},
});

const revisionSlice = createSlice({
  initialState,
  name: "revision",

  reducers: {
    /**
     * @param state The current application state
     * @param action The revision information to add to the store.
     */
    setRegistrationRevision(
      state,
      action: PayloadAction<RegistrationRevision>,
    ) {
      state.registrationRevision = action.payload;
    },

    /**
     * @param state The current application state
     * @param action The revision entities to add to the store.
     */
    addEntities(state, action: PayloadAction<CaptureTreeEntityRevision[]>) {
      // Create a new map, as the store should not be mutated directly
      const newMap: EntityMap = {
        // Add old entries
        ...state.entityMap,
      };

      // Add new entries
      action.payload.forEach((entity) => {
        newMap[entity.id] = entity;
      });

      // Re-generate the world transform cache
      const transformCache = generateRevisionTransformCache(newMap, {});

      state.entityMap = newMap;
      state.transformCache = transformCache;
    },

    /**
     * @param state The current application state
     * @param action The revision entities to replace all previous entities with, in their order.
     */
    setEntities(state, action: PayloadAction<CaptureTreeEntityRevision[]>) {
      state.entityMap = Object.fromEntries(
        action.payload.map((entity) => [entity.id, entity]),
      );
      state.transformCache = generateRevisionTransformCache(
        state.entityMap,
        {},
      );
    },

    /**
     * Updates the given entities and moves them before the given sibling in the entity order,
     * which defines the order of the children of an entity.
     *
     * @param state The current application state
     * @param action The updated entities and the ID of the entity to move them before.
     *  If the sibling is not given or not found, the entities are moved to the end.
     */
    moveEntities(
      state,
      action: PayloadAction<{
        entities: CaptureTreeEntityRevision[];
        beforeSiblingId?: GUID;
      }>,
    ) {
      const { entities, beforeSiblingId } = action.payload;
      const movedIds = new Set(entities.map((entity) => entity.id));
      const keptEntries = Object.entries(state.entityMap).filter(
        ([id]) => !movedIds.has(id),
      );
      const siblingIndex = keptEntries.findIndex(
        ([id]) => id === beforeSiblingId,
      );
      keptEntries.splice(
        siblingIndex === -1 ? keptEntries.length : siblingIndex,
        0,
        ...entities.map((entity): [GUID, CaptureTreeEntityRevision] => [
          entity.id,
          entity,
        ]),
      );

      state.entityMap = Object.fromEntries(keptEntries);
      state.transformCache = generateRevisionTransformCache(
        state.entityMap,
        {},
      );
    },

    /**
     * @param state The current application state
     * @param action The revision entities to remove from the store.
     */
    removeEntities(state, action: PayloadAction<CaptureTreeEntityRevision[]>) {
      // Remove entries
      action.payload.forEach((entity) => {
        delete state.entityMap[entity.id];
      });

      // Re-generate the world transform cache with the kept entities
      state.transformCache = generateRevisionTransformCache(
        state.entityMap,
        {},
      );
    },

    /**
     * @param state The current application state
     * @param action The revision edges to add to the store.
     */
    addRevisionEdges(state, action: PayloadAction<RegistrationEdgeRevision[]>) {
      for (const edge of action.payload) {
        state.edgesMap[edge.id] = edge;
      }
    },

    /**
     * @param state The current application state
     * @param action The revision edges to add to the store.
     */
    addRevisionEdgesFromRegistrationReport(
      state,
      action: PayloadAction<RegistrationEdgeRevision[]>,
    ) {
      for (const edge of action.payload) {
        // Here we are checking if the edge has a transform. This is done due to the fact we trigger this mutation
        // when loading the registration report, which may contain edges without a transformation,
        // and when we explicitly load edges from the backend, which always contains edges with a transformation.
        // Ticket number : https://faro01.atlassian.net/browse/NRT-2093
        if (edge.data?.transformation) {
          // Always assign if transformation exists
          state.edgesMap[edge.id] = edge;
        } else if (!state.edgesMap[edge.id]) {
          // Only assign if it doesn't exist
          state.edgesMap[edge.id] = edge;
        }
      }
    },

    /**
     * @param state The current application state
     * @param action True if the edges are currently being loaded, else false.
     */
    setAreEdgesLoading(state, action: PayloadAction<boolean>) {
      state.areEdgesLoading = action.payload;
    },

    /**
     * @param state The current application state
     * @param action The revision data to set in the store.
     *  Overrides all previous revision data.
     */
    setRevisionData(
      state,
      action: PayloadAction<{
        revision: RegistrationRevision | undefined;
        entities: CaptureTreeEntityRevision[];
        edges: RegistrationEdgeRevision[];
      }>,
    ) {
      state.registrationRevision = action.payload.revision;
      state.entityMap = {};
      action.payload.entities.forEach((entity) => {
        state.entityMap[entity.id] = entity;
      });
      state.edgesMap = {};
      action.payload.edges.forEach((edge) => {
        state.edgesMap[edge.id] = edge;
      });
      state.transformCache = generateRevisionTransformCache(
        state.entityMap,
        {},
      );
    },

    /**
     *
     * @param state The current application state
     * @param action The revision data to add to the store.
     *  Does not override previous revision data, unless the revision changed.
     */
    addRevisionData(
      state,
      action: PayloadAction<{
        revision: RegistrationRevision;
        entities?: CaptureTreeEntityRevision[];
        edges?: RegistrationEdgeRevision[];
      }>,
    ) {
      const isNewRevision =
        state.registrationRevision?.id !== action.payload.revision.id;
      state.registrationRevision = action.payload.revision;

      if (isNewRevision) {
        // Clear entity and edge caches when switching revisions
        state.entityMap = {};
        state.edgesMap = {};
      }

      if (action.payload.entities) {
        action.payload.entities.forEach((entity) => {
          state.entityMap[entity.id] = entity;
        });
        state.transformCache = generateRevisionTransformCache(
          state.entityMap,
          {},
        );
      }
      if (action.payload.edges) {
        action.payload.edges.forEach((edge) => {
          state.edgesMap[edge.id] = edge;
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearStore, () => initialState);
  },
});

export const {
  setRegistrationRevision,
  addEntities,
  setEntities,
  moveEntities,
  removeEntities,
  addRevisionEdges,
  addRevisionEdgesFromRegistrationReport,
  setAreEdgesLoading,
  setRevisionData,
  addRevisionData,
} = revisionSlice.actions;

export const revisionReducer = revisionSlice.reducer;
